import React, { useState, useEffect } from 'react';

const Tutorial = ({ onClose }) => {
  const [currentSection, setCurrentSection] = useState(0);

  // Prevent body scroll while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const sections = [
    {
      title: "👋 Welcome to PySage Tutor",
      content: (
        <div>
          <div style={{ fontSize: '60px', textAlign: 'center', marginBottom: '20px' }}>🎓</div>
          <p style={{ fontSize: '18px', lineHeight: '1.8', marginBottom: '20px' }}>
            Welcome to your personal AI-powered Python learning assistant! This app is designed to help you 
            learn Python programming through guided practice, not by giving you answers directly.
          </p>
          <div style={{ backgroundColor: '#1c2128', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
            <h3 style={{ color: '#f0ad4e', marginTop: 0 }}>🎯 Our Teaching Philosophy</h3>
            <ul style={{ lineHeight: '2', color: '#c9d1d9' }}>
              <li><strong>Guided Discovery:</strong> We help you find answers yourself through hints and questions</li>
              <li><strong>No Direct Answers:</strong> The tutor won't write code for you - you learn by doing</li>
              <li><strong>Adaptive Support:</strong> If you struggle, hints become more detailed automatically</li>
              <li><strong>Memory Tracking:</strong> The app remembers what you've learned and what needs review</li>
            </ul>
          </div>
          <p style={{ color: '#8b949e', fontStyle: 'italic' }}>
            💡 Tip: Use this tutorial anytime by clicking the "📖 Tutorial" button near the logout button.
          </p>
        </div>
      )
    },
    {
      title: "🎮 The Three Learning Modes",
      content: (
        <div>
          <p style={{ fontSize: '16px', marginBottom: '25px', color: '#c9d1d9' }}>
            The app has three different modes, each designed for a specific type of learning. Choose the right mode based on what you need help with:
          </p>
          
          {/* Mode 1 */}
          <div style={{ backgroundColor: '#238636', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '30px' }}>📋</span> Problem Breakdown Mode
            </h3>
            <p style={{ marginBottom: '15px' }}>
              <strong>Best for:</strong> When you have a coding problem/assignment and don't know where to start.
            </p>
            <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '8px' }}>
              <p style={{ margin: 0, color: '#c9d1d9' }}>
                <strong>How it works:</strong><br/>
                1️⃣ Paste your problem description<br/>
                2️⃣ The tutor starts with the first step<br/>
                3️⃣ As you make progress, new steps are revealed<br/>
                4️⃣ Get hints if stuck, then write your code<br/>
                5️⃣ Steps are created progressively as you advance!
              </p>
            </div>
          </div>

          {/* Mode 2 */}
          <div style={{ backgroundColor: '#1f6feb', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '30px' }}>🔧</span> Problem Debugging Mode
            </h3>
            <p style={{ marginBottom: '15px' }}>
              <strong>Best for:</strong> When your code isn't working and you can't figure out why.
            </p>
            <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '8px' }}>
              <p style={{ margin: 0, color: '#c9d1d9' }}>
                <strong>How it works:</strong><br/>
                1️⃣ Paste your broken code and describe the problem<br/>
                2️⃣ The tutor analyzes what might be wrong<br/>
                3️⃣ Instead of fixing it for you, you get guiding questions<br/>
                4️⃣ Learn to debug systematically - a crucial programming skill!
              </p>
            </div>
          </div>

          {/* Mode 3 */}
          <div style={{ backgroundColor: '#8957e5', padding: '20px', borderRadius: '12px' }}>
            <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '30px' }}>🧱</span> Building Block Practice Mode
            </h3>
            <p style={{ marginBottom: '15px' }}>
              <strong>Best for:</strong> Practicing specific Python concepts (loops, functions, lists, etc.)
            </p>
            <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '8px' }}>
              <p style={{ margin: 0, color: '#c9d1d9' }}>
                <strong>Two ways to use this mode:</strong><br/><br/>
                <strong style={{ color: '#f0ad4e' }}>Option 1 - Practice a Concept:</strong><br/>
                1️⃣ Type what concept you want to practice (e.g., "for loops")<br/>
                2️⃣ The tutor creates a custom practice problem<br/>
                3️⃣ Solve it with guided hints<br/><br/>
                <strong style={{ color: '#f0ad4e' }}>Option 2 - Full Problem with Step Practice:</strong><br/>
                1️⃣ Paste a full problem description<br/>
                2️⃣ The tutor breaks it into building block steps<br/>
                3️⃣ Practice each concept/step individually<br/>
                4️⃣ Your progress on each concept is tracked!
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "📝 How to Use Problem Breakdown Mode",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>📋</span>
            <div>
              <h3 style={{ margin: 0, color: '#238636' }}>Problem Breakdown Mode</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Turn complex problems into simple steps</p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Step 1: Enter Your Problem</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', margin: 0 }}>
              Copy and paste your assignment or describe what you need to build. Be as detailed as possible!
            </p>
            <div style={{ backgroundColor: '#0d1117', padding: '10px', borderRadius: '6px', marginTop: '10px', fontFamily: 'monospace', fontSize: '14px' }}>
              <span style={{ color: '#8b949e' }}>Example:</span><br/>
              <span style={{ color: '#c9d1d9' }}>"Write a program that asks the user for a number and prints whether it's even or odd"</span>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Step 2: Steps Appear as You Progress</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              The tutor <strong style={{ color: '#f0ad4e' }}>doesn't show all steps at once!</strong> Instead, steps are created progressively as you make progress. This keeps you focused on one thing at a time.
            </p>
            <div style={{ backgroundColor: '#0d1117', padding: '15px', borderRadius: '6px', marginTop: '10px' }}>
              <div style={{ color: '#f0ad4e', marginBottom: '5px' }}>📌 Plan (grows as you progress):</div>
              <div style={{ color: '#c9d1d9' }}>
                ⏳ Step 1: Get input from user <span style={{ color: '#f0ad4e' }}>← completed!</span><br/>
                ☐ Step 2: Convert to integer <span style={{ color: '#58a6ff' }}>← working on this</span><br/>
                <span style={{ color: '#8b949e' }}>... more steps appear as you advance</span>
              </div>
            </div>
            <p style={{ color: '#8b949e', marginTop: '15px', marginBottom: 0, fontStyle: 'italic' }}>
              💡 This progressive approach helps you stay focused and not feel overwhelmed!
            </p>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Step 3: Work Through Each Step</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '10px' }}>
              For each step:
            </p>
            <ul style={{ color: '#c9d1d9', lineHeight: '2' }}>
              <li>Think about how to solve it</li>
              <li>Click <span style={{ backgroundColor: '#238636', padding: '2px 8px', borderRadius: '4px' }}>💡 Hint</span> if you're stuck</li>
              <li>Write your code in the text area</li>
              <li>Click <span style={{ backgroundColor: '#238636', padding: '2px 8px', borderRadius: '4px' }}>Send Message</span> to check your answer</li>
            </ul>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Step 4: Audit Your Code</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '10px' }}>
              When you've written code, use the <span style={{ backgroundColor: '#1f6feb', padding: '2px 8px', borderRadius: '4px' }}>🔍 Audit Code with Tutor</span> button:
            </p>
            <ul style={{ color: '#c9d1d9', lineHeight: '2' }}>
              <li>The tutor reviews your code for the current step</li>
              <li>Get feedback on correctness, style, and improvements</li>
              <li>Learn best practices as you code!</li>
            </ul>
          </div>

          <h4 style={{ color: '#58a6ff' }}>💾 Download Your Code</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '10px' }}>
              Use the <span style={{ backgroundColor: '#6366f1', padding: '2px 8px', borderRadius: '4px' }}>⬇️ Download</span> button to save your code:
            </p>
            <ul style={{ color: '#c9d1d9', lineHeight: '2' }}>
              <li>Downloads your code as a .py file to your computer</li>
              <li>Save your work anytime!</li>
            </ul>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Step 5: Complete & Move On</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
            <p style={{ color: '#c9d1d9', margin: 0 }}>
              When you get a step right, it shows ⏳ (in progress) and the tutor moves you to the next step. 
              Keep going until you've solved the entire problem! 🎉
            </p>
          </div>
        </div>
      )
    },
    {
      title: "🔧 How to Use Debugging Mode",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>🔧</span>
            <div>
              <h3 style={{ margin: 0, color: '#1f6feb' }}>Problem Debugging Mode</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Learn to find and fix bugs yourself</p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>When to Use This Mode</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <ul style={{ color: '#c9d1d9', lineHeight: '2', margin: 0 }}>
              <li>Your code runs but gives wrong output</li>
              <li>You're getting an error message you don't understand</li>
              <li>Your code doesn't do what you expected</li>
              <li>You're stuck and don't know what's wrong</li>
            </ul>
          </div>

          <h4 style={{ color: '#58a6ff' }}>How to Ask for Help</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '10px' }}>Include this information:</p>
            <div style={{ backgroundColor: '#0d1117', padding: '15px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '13px' }}>
              <span style={{ color: '#f0ad4e' }}>1. Your code:</span><br/>
              <span style={{ color: '#c9d1d9' }}>x = input("Enter number: ")</span><br/>
              <span style={{ color: '#c9d1d9' }}>print(x + 5)</span><br/><br/>
              <span style={{ color: '#f0ad4e' }}>2. What happens:</span><br/>
              <span style={{ color: '#c9d1d9' }}>I get an error: TypeError</span><br/><br/>
              <span style={{ color: '#f0ad4e' }}>3. What you expected:</span><br/>
              <span style={{ color: '#c9d1d9' }}>It should print the number plus 5</span>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>What the Tutor Will Do</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '10px' }}>
              Instead of fixing your code directly, the tutor will:
            </p>
            <ul style={{ color: '#c9d1d9', lineHeight: '2', margin: 0 }}>
              <li>🤔 Ask guiding questions to help you discover the bug</li>
              <li>💡 Give hints about what concept might be involved</li>
              <li>📚 Explain the underlying issue so you learn for next time</li>
              <li>✅ Confirm when you've fixed it correctly</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "🧱 How to Use Building Block Practice",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>🧱</span>
            <div>
              <h3 style={{ margin: 0, color: '#8957e5' }}>Building Block Practice Mode</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Master Python concepts through focused practice OR break down problems</p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>What Are Building Blocks?</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              Building blocks are the fundamental concepts you need to write Python programs:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {['Variables', 'Input/Output', 'If Statements', 'For Loops', 'While Loops', 'Lists', 'Functions', 'Strings'].map(concept => (
                <div key={concept} style={{ backgroundColor: '#0d1117', padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                  <span style={{ color: '#8957e5' }}>⚡</span> {concept}
                </div>
              ))}
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Two Ways to Use This Mode</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#0d1117', borderRadius: '8px', border: '1px solid #8957e5' }}>
              <strong style={{ color: '#8957e5', fontSize: '16px' }}>🎯 Option 1: Practice a Specific Concept</strong>
              <ol style={{ color: '#c9d1d9', lineHeight: '2', margin: '10px 0 0 0', paddingLeft: '20px' }}>
                <li>Type what you want to practice: <code style={{ backgroundColor: '#161b22', padding: '2px 6px', borderRadius: '4px' }}>"I want to practice for loops"</code></li>
                <li>The tutor creates a focused practice problem</li>
                <li>Solve it with hints if needed</li>
                <li>Your mastery of that concept is tracked!</li>
              </ol>
            </div>
            <div style={{ padding: '15px', backgroundColor: '#0d1117', borderRadius: '8px', border: '1px solid #f0ad4e' }}>
              <strong style={{ color: '#f0ad4e', fontSize: '16px' }}>📋 Option 2: Break Down a Full Problem</strong>
              <ol style={{ color: '#c9d1d9', lineHeight: '2', margin: '10px 0 0 0', paddingLeft: '20px' }}>
                <li>Paste your full problem description or assignment</li>
                <li>The tutor identifies all the building block concepts needed</li>
                <li>Practice each concept/step one at a time</li>
                <li>Each concept you practice is tracked separately!</li>
              </ol>
              <p style={{ color: '#8b949e', margin: '10px 0 0 0', fontStyle: 'italic', fontSize: '13px' }}>
                💡 This is great for understanding what concepts a problem requires before coding!
              </p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>📝 Quiz Feature</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              At any point, you can ask the tutor for a quiz to test your understanding:
            </p>
            <ul style={{ color: '#c9d1d9', lineHeight: '2', margin: 0, paddingLeft: '20px' }}>
              <li>🎯 Ask for a quiz on <strong>this/current topic</strong> you're working on</li>
              <li>🎲 Or ask for a quiz on <strong>any topic</strong> needed to solve the problem</li>
              <li>📈 <strong>The more quizzes you take, the better the tutor understands your strengths and weaknesses</strong></li>
              <li>💡 Quiz results automatically adjust hint levels - topics you struggle with get more detailed hints</li>
              <li>🔄 The tutor gives personalized feedback based on your quiz performance</li>
            </ul>
            <div style={{ backgroundColor: '#05966922', border: '1px solid #059669', padding: '12px', borderRadius: '6px', marginTop: '15px' }}>
              <p style={{ color: '#059669', margin: 0, fontSize: '14px' }}>
                <strong>💡 Pro tip:</strong> Take quizzes regularly! They help the tutor personalize your learning experience and identify areas where you need more practice.
              </p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>📊 Building Block Mastery Chart</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              In this mode, you'll see a progress chart showing:
            </p>
            <div style={{ backgroundColor: '#0d1117', padding: '15px', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <div style={{ width: '20px', height: '20px', backgroundColor: '#3fb950', borderRadius: '4px' }}></div>
                <span style={{ color: '#c9d1d9' }}>Correct answers</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '20px', height: '20px', backgroundColor: '#f85149', borderRadius: '4px' }}></div>
                <span style={{ color: '#c9d1d9' }}>Questions to review</span>
              </div>
            </div>
            <p style={{ color: '#8b949e', marginTop: '15px', marginBottom: 0, fontStyle: 'italic' }}>
              💡 Practice topics with more red to improve your skills!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "📑 Course Slides Viewer",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>📑</span>
            <div>
              <h3 style={{ margin: 0, color: '#6366f1' }}>Course Slides</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>View lecture PDFs with built-in study tools</p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>What Is This?</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', margin: 0 }}>
              Click the <span style={{ backgroundColor: '#6366f1', padding: '4px 12px', borderRadius: '4px' }}>📑 Course Slides</span> button
              to open a full-screen PDF viewer for your lecture slides. It comes with powerful study tools built right in!
            </p>
          </div>

          <h4 style={{ color: '#58a6ff' }}>📂 Browsing Slides</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <ul style={{ color: '#c9d1d9', lineHeight: '2.2', margin: 0 }}>
              <li><strong>Sidebar:</strong> Lists all PDF chapters — click any to open it</li>
              <li><strong>Navigation:</strong> Use <strong>◀ Prev</strong> and <strong>Next ▶</strong> buttons to move between pages</li>
              <li><strong>Covered marker:</strong> Click <strong>✅ Covered in Class</strong> to mark pages your instructor has covered — they'll show a green check in the sidebar</li>
            </ul>
          </div>

          <h4 style={{ color: '#58a6ff' }}>🛠️ Built-in Study Tools</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px', borderLeft: '3px solid #3fb950' }}>
              <strong style={{ color: '#3fb950' }}>🧪 Scratch Pad</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                A floating code editor — write and run Python code right next to your slides. Each page saves its own code automatically. You can also download your code as a .py file.
              </p>
            </div>
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px', borderLeft: '3px solid #58a6ff' }}>
              <strong style={{ color: '#58a6ff' }}>📝 Memo</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                A floating notepad for taking notes on each slide page. Notes are saved per page automatically. Download as a .txt file anytime.
              </p>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px', borderLeft: '3px solid #f59e0b' }}>
              <strong style={{ color: '#f59e0b' }}>💬 Explain</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                AI-powered explanation of the current slide. The tutor reads the slide content and explains it in simple, beginner-friendly language. Includes a <strong>3-question quiz</strong> after each explanation to test your understanding!
              </p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>🧠 Quiz After Explanation</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              After each AI explanation, you'll see 3 multiple-choice questions:
            </p>
            <div style={{ backgroundColor: '#0d1117', padding: '15px', borderRadius: '6px' }}>
              <div style={{ marginBottom: '10px' }}>
                <span style={{ color: '#c9d1d9' }}>📌 Click an option to answer</span>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <span style={{ color: '#22c55e' }}>✅ Correct answers</span><span style={{ color: '#c9d1d9' }}> — highlighted in green with explanation</span>
              </div>
              <div>
                <span style={{ color: '#ef4444' }}>❌ Wrong answers</span><span style={{ color: '#c9d1d9' }}> — shown in red, correct answer revealed in green</span>
              </div>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>💡 Tips</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
            <ul style={{ color: '#c9d1d9', lineHeight: '2', margin: 0 }}>
              <li>All floating panels are <strong>draggable</strong> and <strong>resizable</strong> — move them where you like</li>
              <li>Click <strong>🔄 Re-explain</strong> to get a fresh explanation for the same slide</li>
              <li>Scratch pad code and memo notes are <strong>saved per page</strong> — switch pages and your work is still there</li>
              <li>Use the <strong>⬇ Download</strong> buttons to save your code or notes to your computer</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "📚 Course Content Browser",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>📚</span>
            <div>
              <h3 style={{ margin: 0, color: '#a855f7' }}>Course Content</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Learn topics directly with AI tutoring</p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>What Is This?</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', margin: 0 }}>
              Click the <span style={{ backgroundColor: '#a855f7', padding: '4px 12px', borderRadius: '4px' }}>📚 Course Content</span> button 
              to open a full-screen view of all course slides and topics. Pick any topic and get personalized tutoring!
            </p>
          </div>

          <h4 style={{ color: '#58a6ff' }}>How It Works</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <ol style={{ color: '#c9d1d9', lineHeight: '2.2', margin: 0, paddingLeft: '20px' }}>
              <li><strong>Browse slides:</strong> See all course chapters organized by slides</li>
              <li><strong>Pick a topic:</strong> Click any topic to start learning</li>
              <li><strong>Get taught:</strong> The AI tutor explains the concept with examples</li>
              <li><strong>Choose practice:</strong> After learning, choose Quiz or Coding exercises</li>
              <li><strong>One at a time:</strong> Questions are given one at a time, not all at once</li>
            </ol>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Learning Flow</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px', borderLeft: '3px solid #3fb950' }}>
              <strong style={{ color: '#3fb950' }}>Step 1: Teaching</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                Introduction → Core concepts → Code examples with output
              </p>
            </div>
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px', borderLeft: '3px solid #58a6ff' }}>
              <strong style={{ color: '#58a6ff' }}>Step 2: Choose Practice</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                Select: Quiz (multiple choice) or Coding (write code) or Both
              </p>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px', borderLeft: '3px solid #f0ad4e' }}>
              <strong style={{ color: '#f0ad4e' }}>Step 3: Interactive Practice</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                Answer questions one at a time, get feedback, ask for hints if needed
              </p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>💡 Progressive Hints</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              If you're stuck and ask for help, the tutor gives <strong>progressive hints</strong> instead of the answer:
            </p>
            <div style={{ backgroundColor: '#0d1117', padding: '15px', borderRadius: '6px' }}>
              <div style={{ marginBottom: '10px' }}>
                <span style={{ color: '#3fb950' }}>Hint 1:</span> <span style={{ color: '#c9d1d9' }}>General guidance (think about...)</span>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <span style={{ color: '#f0ad4e' }}>Hint 2:</span> <span style={{ color: '#c9d1d9' }}>More specific help (here's a clue...)</span>
              </div>
              <div>
                <span style={{ color: '#f85149' }}>Hint 3:</span> <span style={{ color: '#c9d1d9' }}>Full solution with explanation</span>
              </div>
            </div>
            <p style={{ color: '#8b949e', marginTop: '15px', marginBottom: 0, fontStyle: 'italic' }}>
              This encourages you to think before getting the answer!
            </p>
          </div>

          <h4 style={{ color: '#58a6ff' }}>⌨️ Input Tips</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
            <ul style={{ color: '#c9d1d9', lineHeight: '2', margin: 0 }}>
              <li><strong>Multi-line input:</strong> The text area is designed for code - write multiple lines!</li>
              <li><strong>Send message:</strong> Press <code style={{ backgroundColor: '#161b22', padding: '2px 6px', borderRadius: '4px' }}>Ctrl+Enter</code> (or <code style={{ backgroundColor: '#161b22', padding: '2px 6px', borderRadius: '4px' }}>⌘+Enter</code> on Mac)</li>
              <li><strong>New line:</strong> Just press <code style={{ backgroundColor: '#161b22', padding: '2px 6px', borderRadius: '4px' }}>Enter</code> to add a new line</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "✅ Progress Tracking Checkmarks",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>✅</span>
            <div>
              <h3 style={{ margin: 0, color: '#3fb950' }}>Track Your Study Progress</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Mark chapters and topics as studied or skipped</p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>What Are The Checkmarks?</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', margin: 0 }}>
              In the Course Content sidebar, you'll see <strong>checkboxes</strong> next to each chapter and topic.
              These let you track what you've studied, helping you stay organized and see your progress at a glance!
            </p>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Three States</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#8b949e', marginBottom: '15px' }}>Click a checkbox to cycle through these states:</p>
            
            <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '20px', width: '24px', height: '24px', border: '2px solid #6b7280', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}></span>
              <div>
                <strong style={{ color: '#c9d1d9' }}>📖 Not Started</strong>
                <p style={{ color: '#8b949e', margin: '5px 0 0 0', fontSize: '14px' }}>
                  Empty checkbox - You haven't studied this yet
                </p>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '20px', width: '24px', height: '24px', backgroundColor: '#22c55e', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>✓</span>
              <div>
                <strong style={{ color: '#22c55e' }}>✅ Studied</strong>
                <p style={{ color: '#8b949e', margin: '5px 0 0 0', fontSize: '14px' }}>
                  Green checkmark - You've studied and understand this material
                </p>
              </div>
            </div>
            
            <div style={{ padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '20px', width: '24px', height: '24px', backgroundColor: '#f97316', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>⊘</span>
              <div>
                <strong style={{ color: '#f97316' }}>⏭️ Skipped</strong>
                <p style={{ color: '#8b949e', margin: '5px 0 0 0', fontSize: '14px' }}>
                  Orange skip mark - You've decided to skip this for now (crossed out text)
                </p>
              </div>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Chapter vs Topic Checkmarks</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px', borderLeft: '3px solid #a855f7' }}>
              <strong style={{ color: '#a855f7' }}>📑 Chapter Checkmarks</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                Mark an entire chapter (slide) as studied or skipped. Independent from individual topics.
              </p>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px', borderLeft: '3px solid #3fb950' }}>
              <strong style={{ color: '#3fb950' }}>📝 Topic Checkmarks</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                Mark individual topics within a chapter. Each topic can be tracked separately.
              </p>
            </div>
            <p style={{ color: '#8b949e', marginTop: '15px', marginBottom: 0, fontStyle: 'italic', fontSize: '13px' }}>
              💡 Chapter and topic checkmarks are independent - marking a chapter doesn't affect its topics, and vice versa.
            </p>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Progress Counter</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              Each chapter shows a progress badge like <span style={{ backgroundColor: '#374151', padding: '2px 8px', borderRadius: '10px', fontSize: '13px' }}><span style={{ color: '#22c55e' }}>✓2</span> <span style={{ color: '#f97316' }}>⊘1</span>/6</span>
            </p>
            <ul style={{ color: '#c9d1d9', lineHeight: '2', margin: 0 }}>
              <li><span style={{ color: '#22c55e' }}>✓2</span> = 2 topics marked as studied</li>
              <li><span style={{ color: '#f97316' }}>⊘1</span> = 1 topic marked as skipped</li>
              <li><span style={{ color: '#8b949e' }}>/6</span> = total number of topics in the chapter</li>
            </ul>
          </div>

          <div style={{ backgroundColor: '#3fb95022', border: '1px solid #3fb950', padding: '15px', borderRadius: '8px' }}>
            <p style={{ color: '#3fb950', margin: 0 }}>
              💾 <strong>Auto-saved!</strong> Your progress is automatically saved to your browser. 
              You can close the app and come back later - your checkmarks will still be there!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "🎯 Try It, Scratch Pad & Settings",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>🛠️</span>
            <div>
              <h3 style={{ margin: 0, color: '#3fb950' }}>Helpful Tools in Course Content</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Run code, practice, and customize your experience</p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>🎯 Try It Buttons</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              Every code example in the tutor's explanations has a <span style={{ backgroundColor: '#238636', padding: '4px 10px', borderRadius: '4px' }}>▶ Try It</span> button!
            </p>
            <ul style={{ color: '#c9d1d9', lineHeight: '2', margin: 0 }}>
              <li><strong>Click "Try It":</strong> Opens a code editor with the example pre-loaded</li>
              <li><strong>Run the code:</strong> Execute Python code directly in your browser</li>
              <li><strong>Edit & experiment:</strong> Modify the code and see what happens</li>
              <li><strong>Send to Tutor:</strong> Click "💬 Send to Tutor" to ask for help with the code</li>
            </ul>
            <p style={{ color: '#8b949e', marginTop: '15px', marginBottom: 0, fontStyle: 'italic' }}>
              💡 The best way to learn is to experiment! Change values, add lines, and see the results.
            </p>
          </div>

          <h4 style={{ color: '#79c0ff' }}>🔍 Step-by-Step Visualizer</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              Click the <span style={{ backgroundColor: '#1d3a5f', border: '1px solid rgba(255,240,150,0.75)', padding: '4px 10px', borderRadius: '4px', color: '#79c0ff' }}>🔍 Visualize</span> button in the code editor to watch your code execute <strong>one line at a time</strong>.
            </p>
            <ul style={{ color: '#c9d1d9', lineHeight: '2', margin: 0 }}>
              <li><strong>Highlighted line:</strong> See exactly which line is currently executing</li>
              <li><strong>Variables panel:</strong> Watch every variable appear and change value in real time — newly changed values are highlighted in yellow</li>
              <li><strong>Output panel:</strong> See what has been printed to the screen so far at each step</li>
              <li><strong>Prev / Next buttons:</strong> Step forward and backward freely — or use ← → arrow keys</li>
              <li><strong>Progress bar:</strong> Shows how far through the program you are</li>
              <li><strong>✓ Program complete:</strong> A green checkmark appears when the last line finishes</li>
            </ul>
            <div style={{ backgroundColor: '#0d1117', padding: '12px', borderRadius: '6px', marginTop: '15px' }}>
              <strong style={{ color: '#f0c040' }}>⚠️ Loops with many iterations</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                The visualizer captures up to <strong>500 steps</strong>. If your loop runs more than that, you'll see a warning banner and only the first 500 steps will be shown. Try a smaller range (e.g. <code style={{ color: '#79c0ff' }}>range(5)</code>) to see the full trace.
              </p>
            </div>
            <div style={{ backgroundColor: '#0d1117', padding: '12px', borderRadius: '6px', marginTop: '10px' }}>
              <strong style={{ color: '#3fb950' }}>💡 Code with input()</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                If your code calls <code style={{ color: '#79c0ff' }}>input()</code>, a dialog will appear asking you to type the values ahead of time — one per line — before the visualizer starts.
              </p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>💻 Scratch Pad</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              Click the <span style={{ backgroundColor: '#6366f1', padding: '4px 10px', borderRadius: '4px' }}>💻 Scratch Pad</span> button in the header to open a practice area.
            </p>
            <ul style={{ color: '#c9d1d9', lineHeight: '2', margin: 0 }}>
              <li><strong>Write your own code:</strong> Test ideas without leaving the lesson</li>
              <li><strong>Always available:</strong> Access it anytime from the top header</li>
              <li><strong>Run & test:</strong> Execute your code and see the output</li>
              <li><strong>Perfect for practice:</strong> Try concepts right after learning them</li>
            </ul>
          </div>

          <h4 style={{ color: '#58a6ff' }}>⚙️ Settings</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              Click the <span style={{ backgroundColor: '#21262d', border: '2px solid #a89860', padding: '4px 10px', borderRadius: '4px' }}>⚙️ Settings</span> button to customize your experience:
            </p>
            <div style={{ backgroundColor: '#0d1117', padding: '15px', borderRadius: '6px' }}>
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#3fb950' }}>🔊 Speech Settings</strong>
                <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                  • Enable/disable the "Listen" button on tutor messages<br/>
                  • Adjust speech speed (slow to fast)
                </p>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#58a6ff' }}>🎨 Theme</strong>
                <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                  Choose: Dark, Light, Sepia, or High Contrast
                </p>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#f0ad4e' }}>📝 Text Size</strong>
                <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                  Adjust font size for comfortable reading (14px - 20px)
                </p>
              </div>
              <div>
                <strong style={{ color: '#d2a8ff' }}>🎓 Programming Exercise Tutor Mode</strong>
                <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                  Choose how the tutor guides you through chapter exercises (see details below)
                </p>
              </div>
            </div>
            <p style={{ color: '#8b949e', marginTop: '15px', marginBottom: 0, fontStyle: 'italic' }}>
              💡 Your settings are saved automatically and remembered next time you visit!
            </p>
          </div>

          <h4 style={{ color: '#d2a8ff' }}>🎓 Programming Exercise Tutor Modes</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              When doing <strong>Programming Exercises</strong> in Course Content, you can choose how the tutor guides you:
            </p>
            
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#4a6b3a', borderRadius: '6px' }}>
              <strong style={{ color: '#fff' }}>📝 Step-by-Step Discovery</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                <em>Best for beginners!</em> The tutor asks "What should be the first step?" and guides you to 
                identify each step yourself before coding. Builds problem-solving skills.
              </p>
            </div>
            
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#3a5a6b', borderRadius: '6px' }}>
              <strong style={{ color: '#fff' }}>📋 Show All Steps</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                <em>Good for review!</em> The tutor shows all steps upfront, then helps you work through each one.
                Useful when you want to see the full plan first.
              </p>
            </div>
            
            <div style={{ padding: '12px', backgroundColor: '#5a4a6b', borderRadius: '6px' }}>
              <strong style={{ color: '#fff' }}>🧠 Adaptive</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                <em>Smart mode!</em> The tutor adjusts based on your responses - gives more guidance if you struggle, 
                challenges you more if you're doing well.
              </p>
            </div>
            
            <p style={{ color: '#f0883e', marginTop: '15px', marginBottom: 0, fontSize: '13px' }}>
              ⚠️ Note: Changes take effect on the next exercise you start.
            </p>
          </div>

          <h4 style={{ color: '#58a6ff' }}>🔊 Listen Button</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              Each tutor message has a <span style={{ backgroundColor: '#fff3b0', color: '#000', padding: '4px 10px', borderRadius: '4px' }}>🔊 Listen</span> button:
            </p>
            <ul style={{ color: '#c9d1d9', lineHeight: '2', margin: 0 }}>
              <li><strong>Click to hear:</strong> The tutor reads the explanation aloud</li>
              <li><strong>Click again to stop:</strong> Stop playback anytime</li>
              <li><strong>Adjust speed:</strong> Change speech speed in Settings</li>
              <li><strong>Turn off:</strong> Disable in Settings if you don't need it</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "📚 Knowledge Limit Feature",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>📚</span>
            <div>
              <h3 style={{ margin: 0, color: '#f0ad4e' }}>Knowledge Limit</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Control what concepts the tutor can use</p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>What Is This?</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', margin: 0 }}>
              The <strong style={{ color: '#f0ad4e' }}>Knowledge Limit</strong> dropdown lets you restrict the tutor to only use 
              concepts you've already learned in class. This prevents the tutor from suggesting solutions 
              using things you haven't been taught yet!
            </p>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Options Explained</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
              <strong style={{ color: '#3fb950' }}>🚫 No Limit</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                The tutor can use any Python concept. Good for advanced learners or self-study.
              </p>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
              <strong style={{ color: '#f0ad4e' }}>📖 Up to Slide X</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                Manually select which class slide to limit to. Useful for reviewing past topics.
              </p>
            </div>
          </div>

          <div style={{ backgroundColor: '#f0ad4e22', border: '1px solid #f0ad4e', padding: '15px', borderRadius: '8px' }}>
            <p style={{ color: '#f0ad4e', margin: 0, fontWeight: 'bold' }}>
              💡 Example: If your class just covered "for loops" but hasn't learned "functions" yet, 
              set the limit to that slide. The tutor won't suggest using functions in your solutions!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "💡 The Hint Button",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>💡</span>
            <div>
              <h3 style={{ margin: 0, color: '#f0ad4e' }}>Getting Hints</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Smart help that adapts to your needs</p>
            </div>
          </div>

          <h4 style={{ color: '#58a6ff' }}>How Hints Work</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              Click the <span style={{ backgroundColor: '#238636', padding: '4px 12px', borderRadius: '4px' }}>💡 Hint</span> button 
              whenever you're stuck. The app tracks your progress and adjusts hint detail automatically!
            </p>
          </div>

          <h4 style={{ color: '#58a6ff' }}>Adaptive Hint Levels</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#8b949e', marginBottom: '15px' }}>Based on your history with each topic:</p>
            
            <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#3fb95022', border: '1px solid #3fb950', borderRadius: '6px' }}>
              <strong style={{ color: '#3fb950' }}>🟢 MINIMAL Hints</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                You're doing great with this topic! Just a small nudge to keep you thinking.
              </p>
            </div>
            
            <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f0ad4e22', border: '1px solid #f0ad4e', borderRadius: '6px' }}>
              <strong style={{ color: '#f0ad4e' }}>🟡 MODERATE Hints</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                You sometimes struggle here. More detailed guidance with examples.
              </p>
            </div>
            
            <div style={{ padding: '12px', backgroundColor: '#f8514922', border: '1px solid #f85149', borderRadius: '6px' }}>
              <strong style={{ color: '#f85149' }}>🔴 DETAILED Hints</strong>
              <p style={{ color: '#c9d1d9', margin: '5px 0 0 0', fontSize: '14px' }}>
                This topic is challenging for you. Step-by-step walkthroughs with syntax examples.
              </p>
            </div>
          </div>

          <div style={{ backgroundColor: '#1c212855', padding: '15px', borderRadius: '8px' }}>
            <p style={{ color: '#8b949e', margin: 0, fontStyle: 'italic' }}>
              🧠 The more you practice correctly, the less detailed hints become - you're building independence!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "📊 Analytics Dashboards",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>📊</span>
            <div>
              <h3 style={{ margin: 0, color: '#58a6ff' }}>Track Your Progress</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Four powerful analytics tools</p>
            </div>
          </div>

          <div style={{ display: 'grid', gap: '15px' }}>
            {/* Full Analytics */}
            <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#238636' }}>📊 Quiz Analytics</h4>
              <p style={{ color: '#c9d1d9', margin: '0 0 10px 0', fontSize: '14px' }}>
                <strong>Detailed performance view:</strong> See all your mastery data, history, and performance metrics. 
                Track exactly what topics you've practiced and how well you did.
              </p>
              <p style={{ color: '#c9d1d9', margin: 0, fontSize: '14px' }}>
                <strong>📋 Study Recommendation:</strong> Click the "Get Study Recommendation" button to receive 
                AI-powered personalized suggestions on what to study next based on your performance!
              </p>
            </div>

            {/* My Activity Stats */}
            <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#238636' }}>📈 My Activity Stats</h4>
              <p style={{ color: '#c9d1d9', margin: 0, fontSize: '14px' }}>
                <strong>Your personal dashboard:</strong> Tracks your activities.
              </p>
            </div>

            {/* Struggle Insights */}
            <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#238636' }}>📝 Past Quiz Review</h4>
              <p style={{ color: '#c9d1d9', margin: '0 0 10px 0', fontSize: '14px' }}>
                <strong>Learn from mistakes:</strong> Shows quizzes where you made mistakes in different concepts.
              </p>
              <p style={{ color: '#c9d1d9', margin: 0, fontSize: '14px' }}>
                <strong>✓ Mark as Reviewed:</strong> Click the <span style={{ backgroundColor: '#374151', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>✓</span> button next to any quiz you've reviewed. 
                It will turn green so you know you've already gone over it.
              </p>
            </div>

            {/* Knowledge Health */}
            <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#059669' }}>🧠 Knowledge Health</h4>
              <p style={{ color: '#c9d1d9', margin: '0 0 10px 0', fontSize: '14px' }}>
                <strong>Memory strength tracker:</strong> Shows how well you remember each topic based on 
                spaced repetition science. Topics fade over time - review them before they're forgotten!
              </p>
              <p style={{ color: '#c9d1d9', margin: 0, fontSize: '14px' }}>
                <strong>✓ Mark as Mastered:</strong> Click the <span style={{ backgroundColor: '#374151', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>✓</span> button next to any topic you've truly mastered. 
                It will turn gray so you know you don't need to worry about it anymore.
              </p>
            </div>

            {/* Knowledge States */}
            <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', border: '1px solid rgba(253,224,71,0.25)' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#fef08a' }}>🔮 Knowledge States</h4>
              <p style={{ color: '#c9d1d9', margin: '0 0 10px 0', fontSize: '14px' }}>
                <strong>AI-estimated learning stage:</strong> Uses a Hidden Markov Model (HMM) to estimate 
                which learning stage you're currently in for each topic, based on your quiz and chat history.
              </p>
              <p style={{ color: '#c9d1d9', margin: '0 0 10px 0', fontSize: '14px' }}>
                <strong>Four stages:</strong>
              </p>
              <ul style={{ color: '#c9d1d9', margin: '0 0 10px 0', fontSize: '14px', paddingLeft: '20px' }}>
                <li><strong style={{ color: '#60a5fa' }}>🌱 Novice:</strong> Just starting — a beginner-level question will be sent</li>
                <li><strong style={{ color: '#a78bfa' }}>📖 Practicing:</strong> Building understanding — a practice question to reinforce it</li>
                <li><strong style={{ color: '#34d399' }}>⚡ Mastered:</strong> Strong understanding — a challenging question to test mastery</li>
                <li><strong style={{ color: '#f87171' }}>🔄 Forgetting:</strong> Haven't seen it in a while — a review question to refresh</li>
              </ul>
              <p style={{ color: '#c9d1d9', margin: 0, fontSize: '14px' }}>
                <strong>💬 Click any topic card</strong> to instantly send a practice question tailored 
                to your current stage — no need to type anything!
              </p>
            </div>

            {/* Learning Progress Map */}
            <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#f59e0b' }}>🗺️ Learning Progress Map</h4>
              <p style={{ color: '#c9d1d9', margin: '0 0 10px 0', fontSize: '14px' }}>
                <strong>Visual struggle heatmap:</strong> See exactly where you're struggling with a color-coded map. 
                Topics are categorized by error type - syntax errors, logic errors, and runtime errors.
              </p>
              <p style={{ color: '#c9d1d9', margin: '0 0 10px 0', fontSize: '14px' }}>
                <strong>Color coding:</strong> 
                <span style={{ color: '#ef4444' }}> Red = many errors</span>, 
                <span style={{ color: '#f59e0b' }}> Yellow = some errors</span>, 
                <span style={{ color: '#22c55e' }}> Green = few errors</span>. 
                Focus your practice on red areas!
              </p>
              <p style={{ color: '#c9d1d9', margin: 0, fontSize: '14px' }}>
                <strong>📋 Study Recommendation:</strong> Click the "Get Study Recommendation" button for 
                AI-powered suggestions on what to focus on based on your struggle patterns!
              </p>
            </div>

            {/* Study Advice */}
            <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#fef08a' }}>🎯 Study Advice</h4>
              <p style={{ color: '#c9d1d9', margin: '0 0 10px 0', fontSize: '14px' }}>
                <strong>Personalized AI study guide:</strong> Click <span style={{ backgroundColor: '#374151', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>🎯 Study Advice</span> in the sidebar to instantly generate a one-page study plan tailored to you. It analyzes your quiz history, struggle log, and past Q&amp;A to pinpoint your weakest topics and tell you exactly what to review next.
              </p>
              <p style={{ color: '#f87171', margin: 0, fontSize: '12px', fontStyle: 'italic' }}>
                ⚠️ AI-generated — use as a guide, not a guarantee.
              </p>
            </div>

            {/* Practice Exam */}
            <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#238636' }}>📝 Practice Exam</h4>
              <p style={{ color: '#c9d1d9', margin: '0 0 10px 0', fontSize: '14px' }}>
                <strong>Test yourself:</strong> Take a practice exam tailored to your needs. Choose from four modes:
              </p>
              <ul style={{ color: '#c9d1d9', margin: 0, fontSize: '14px', paddingLeft: '20px' }}>
                <li><strong style={{ color: '#fbbf24' }}>🎯 Personalized:</strong> Questions focus on your weak areas from the heatmap</li>
                <li><strong style={{ color: '#60a5fa' }}>📚 Standard:</strong> Balanced mix with emphasis on recent chapters</li>
                <li><strong style={{ color: '#a78bfa' }}>✏️ Custom:</strong> Pick specific chapters you want to be tested on</li>
                <li><strong style={{ color: '#10b981' }}>🧩 Similar Question/Subject:</strong> Paste any question or topic — the AI generates 6 multiple choice questions + 1 coding question closely matching it. Optionally restrict to your current chapter scope to stay within your knowledge limit.</li>
              </ul>
            </div>
          </div>

          <div style={{ backgroundColor: '#05966922', border: '1px solid #059669', padding: '15px', borderRadius: '8px', marginTop: '15px' }}>
            <p style={{ color: '#059669', margin: 0 }}>
              💡 <strong>Pro tip:</strong> Check "Learning Progress Map" to see your weak areas, then use "Practice Exam" in Personalized mode to target those specific topics!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "🏆 Badges & Points",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>🏆</span>
            <div>
              <h3 style={{ margin: 0, color: '#f5c542' }}>Earn Points & Level Up</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Track your achievements and unlock badges</p>
            </div>
          </div>

          <div style={{ backgroundColor: '#1c2128', padding: '20px', borderRadius: '8px', marginBottom: '15px' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#f5e6a3' }}>🎖️ Badge Tiers</h4>
            <p style={{ color: '#c9d1d9', margin: '0 0 12px 0', fontSize: '14px' }}>
              As you earn points, you progress through badge tiers:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '18px' }}>🌱</span>
                <span style={{ color: '#9ca3af', fontSize: '13px' }}>No Badge — Starting out</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '18px' }}>🥉</span>
                <span style={{ color: '#cd7f32', fontSize: '13px' }}>Bronze — 240 pts</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '18px' }}>🥈</span>
                <span style={{ color: '#c0c0c0', fontSize: '13px' }}>Silver — 350 pts</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '18px' }}>🥇</span>
                <span style={{ color: '#ffd700', fontSize: '13px' }}>Gold — 480 pts</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '18px' }}>🪙</span>
                <span style={{ color: '#e5e4e2', fontSize: '13px' }}>Platinum — 680 pts</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '18px' }}>💎</span>
                <span style={{ color: '#b9f2ff', fontSize: '13px' }}>Diamond — 960 pts (repeats every 430!)</span>
              </div>
            </div>
          </div>

          <div style={{ backgroundColor: '#1c2128', padding: '20px', borderRadius: '8px', marginBottom: '15px' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#f5e6a3' }}>💡 How to Earn Points</h4>
            <div style={{ display: 'grid', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '16px' }}>📝</span>
                <span style={{ color: '#c9d1d9', fontSize: '13px' }}><strong>Quiz Points</strong> — Pass quizzes on topics you've practiced</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '16px' }}>💬</span>
                <span style={{ color: '#c9d1d9', fontSize: '13px' }}><strong>Answer Points</strong> — Answer tutor questions correctly</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '16px' }}>📖</span>
                <span style={{ color: '#c9d1d9', fontSize: '13px' }}><strong>Review Points</strong> — Engage with topic reviews</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '16px' }}>🎓</span>
                <span style={{ color: '#c9d1d9', fontSize: '13px' }}><strong>Exam Points</strong> — Score well on practice exams</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '16px' }}>🧩</span>
                <span style={{ color: '#c9d1d9', fontSize: '13px' }}><strong>Code Points</strong> — Submit working code to the tutor</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '16px' }}>🔥</span>
                <span style={{ color: '#c9d1d9', fontSize: '13px' }}><strong>Streak Points</strong> — Log in on consecutive days</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', backgroundColor: '#0d1117', borderRadius: '6px' }}>
                <span style={{ fontSize: '16px' }}>⏱️</span>
                <span style={{ color: '#c9d1d9', fontSize: '13px' }}><strong>Session Points</strong> — Study for extended periods</span>
              </div>
            </div>
          </div>

          <div style={{ backgroundColor: '#1c2128', padding: '20px', borderRadius: '8px', marginBottom: '15px' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#f5e6a3' }}>📍 How to View Your Badge</h4>
            <p style={{ color: '#c9d1d9', margin: 0, fontSize: '14px' }}>
              Click the <span style={{ backgroundColor: '#374151', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>🏆 My Badge</span> button 
              in the sidebar to see your current badge, total points, detailed breakdown, streak info, and progress toward the next tier.
            </p>
          </div>

          <div style={{ backgroundColor: '#05966922', border: '1px solid #059669', padding: '15px', borderRadius: '8px' }}>
            <p style={{ color: '#059669', margin: 0 }}>
              💡 <strong>Pro tip:</strong> Accuracy and consistency earn more than volume. Focus on understanding each topic deeply, 
              keep your daily streak going, and the points will follow!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "🆕 Starting Fresh",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>🆕</span>
            <div>
              <h3 style={{ margin: 0, color: '#58a6ff' }}>New Problem Button</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Start fresh with a new problem</p>
            </div>
          </div>

          <div style={{ backgroundColor: '#1c2128', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              When you're done with a problem or want to try something different, click the 
              <span style={{ backgroundColor: '#1f6feb', padding: '4px 12px', borderRadius: '4px', marginLeft: '8px' }}>🆕 New Problem</span> button.
            </p>
            <p style={{ color: '#c9d1d9', margin: 0 }}>
              This will:
            </p>
            <ul style={{ color: '#c9d1d9', lineHeight: '2' }}>
              <li>Clear the current conversation</li>
              <li>Reset the plan panel</li>
              <li>Keep your mastery progress (it's saved!)</li>
              <li>Let you enter a fresh problem</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "🔔 Notifications",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>🔔</span>
            <div>
              <h3 style={{ margin: 0, color: '#f0ad4e' }}>Smart Notifications</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Never forget what you've learned</p>
            </div>
          </div>

          <div style={{ backgroundColor: '#1c2128', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#c9d1d9', marginBottom: '15px' }}>
              The app can alert you when your knowledge of a topic is fading! Based on memory science 
              (the "forgetting curve"), it knows when you should review something.
            </p>
            <div style={{ backgroundColor: '#0d1117', padding: '15px', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '24px' }}>🔔</span>
                <span style={{ color: '#3fb950' }}>Notifications ON</span>
              </div>
              <p style={{ color: '#8b949e', margin: 0, fontSize: '14px' }}>
                You'll see alerts when topics need review
              </p>
            </div>
            <div style={{ backgroundColor: '#0d1117', padding: '15px', borderRadius: '6px', marginTop: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '24px' }}>🔕</span>
                <span style={{ color: '#8b949e' }}>Notifications OFF</span>
              </div>
              <p style={{ color: '#8b949e', margin: 0, fontSize: '14px' }}>
                No alerts - you can still check Knowledge Health manually
              </p>
            </div>
          </div>

          <p style={{ color: '#8b949e' }}>
            Toggle notifications using the 🔔/🔕 button near the logout button.
          </p>
        </div>
      )
    },
    {
      title: "💬 Conversation Tips",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>💬</span>
            <div>
              <h3 style={{ margin: 0, color: '#58a6ff' }}>Getting the Best Help</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Tips for effective learning</p>
            </div>
          </div>

          <h4 style={{ color: '#3fb950' }}>✅ Do This</h4>
          <div style={{ backgroundColor: '#3fb95022', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <ul style={{ color: '#c9d1d9', lineHeight: '2.2', margin: 0 }}>
              <li><strong>Be specific:</strong> "My for loop runs forever" is better than "it doesn't work"</li>
              <li><strong>Show your attempt:</strong> Even if wrong, sharing your code helps the tutor guide you</li>
              <li><strong>Explain your thinking:</strong> "I tried X because I thought Y" helps identify misconceptions</li>
              <li><strong>Ask "why":</strong> Understanding concepts beats memorizing solutions</li>
              <li><strong>Use hints progressively:</strong> Try yourself first, then ask for hints</li>
            </ul>
          </div>

          <h4 style={{ color: '#f85149' }}>❌ Avoid This</h4>
          <div style={{ backgroundColor: '#f8514922', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <ul style={{ color: '#c9d1d9', lineHeight: '2.2', margin: 0 }}>
              <li><strong>"Just give me the answer":</strong> The tutor is designed to teach, not solve for you</li>
              <li><strong>Vague questions:</strong> "Help me with Python" - be more specific!</li>
              <li><strong>Skipping steps:</strong> Each step builds on the previous one</li>
              <li><strong>Ignoring hints:</strong> Hints contain valuable learning - read them carefully!</li>
            </ul>
          </div>

          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px' }}>
            <p style={{ color: '#f0ad4e', margin: 0 }}>
              🎓 <strong>Remember:</strong> The goal isn't to finish fast - it's to understand deeply. 
              Taking time to work through problems builds real programming skills!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "⚠️ Report Issue",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>⚠️</span>
            <div>
              <h3 style={{ margin: 0, color: '#f59e0b' }}>Found a Bug or Problem?</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Help us improve by reporting issues</p>
            </div>
          </div>

          <div style={{ backgroundColor: '#f59e0b22', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
            <h4 style={{ color: '#f59e0b', marginTop: 0 }}>🔍 How to Report an Issue</h4>
            <ol style={{ color: '#c9d1d9', lineHeight: '2', margin: 0, paddingLeft: '20px' }}>
              <li>Click the <strong style={{ color: '#f59e0b' }}>⚠️ Report Issue</strong> button in the top-right corner</li>
              <li>Select a <strong>criticality level</strong>:
                <ul style={{ marginTop: '5px' }}>
                  <li><span style={{ color: '#22c55e' }}>🟢 Low</span> - Minor inconvenience, cosmetic issues</li>
                  <li><span style={{ color: '#f59e0b' }}>🟡 Medium</span> - Something isn't working as expected</li>
                  <li><span style={{ color: '#ef4444' }}>🔴 High</span> - Major functionality broken, can't use the app</li>
                </ul>
              </li>
              <li>Describe the problem in detail:
                <ul style={{ marginTop: '5px' }}>
                  <li>What were you trying to do?</li>
                  <li>What happened instead?</li>
                  <li>Any error messages you saw?</li>
                </ul>
              </li>
              <li>Click <strong>Submit Report</strong></li>
            </ol>
          </div>

          <div style={{ backgroundColor: '#1c2128', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
            <h4 style={{ color: '#58a6ff', marginTop: 0 }}>📝 What to Report</h4>
            <ul style={{ color: '#c9d1d9', lineHeight: '2' }}>
              <li><strong>Bugs:</strong> Things that don't work correctly</li>
              <li><strong>Confusing behavior:</strong> Features that are hard to understand</li>
              <li><strong>Wrong feedback:</strong> Tutor gave incorrect information</li>
              <li><strong>Display issues:</strong> Things look wrong or broken</li>
              <li><strong>Suggestions:</strong> Ideas for improvement</li>
            </ul>
          </div>

          <div style={{ backgroundColor: '#3fb95022', padding: '15px', borderRadius: '8px' }}>
            <p style={{ color: '#3fb950', margin: 0 }}>
              💚 <strong>Your feedback helps!</strong> Every report is reviewed to make the app better for everyone.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "📁 Class Codes",
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
            <span style={{ fontSize: '50px' }}>📁</span>
            <div>
              <h3 style={{ margin: 0, color: '#3fb950' }}>Your Code Workspace</h3>
              <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Access class files and manage your own code</p>
            </div>
          </div>

          <div style={{ backgroundColor: '#1c2128', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
            <h4 style={{ color: '#58a6ff', marginTop: 0 }}>🔍 How to Access Class Codes</h4>
            <ol style={{ color: '#c9d1d9', lineHeight: '2', margin: 0, paddingLeft: '20px' }}>
              <li>Click the <strong style={{ color: '#3fb950' }}>📁 Class Codes</strong> button in the top menu</li>
              <li>A PyCharm-style IDE window opens with two file sections</li>
              <li>Browse, view, and run Python files</li>
            </ol>
          </div>

          <h4 style={{ color: '#f0ad4e' }}>📂 Two File Sections</h4>
          <div style={{ display: 'grid', gap: '15px', marginBottom: '20px' }}>
            <div style={{ backgroundColor: '#238636', padding: '15px', borderRadius: '8px' }}>
              <h5 style={{ margin: '0 0 10px 0', color: '#ffffff' }}>📚 CLASS FILES</h5>
              <p style={{ margin: 0, color: '#c9d1d9', lineHeight: '1.8' }}>
                Files shared by your instructor. You can <strong>view</strong>, <strong>run</strong>, and <strong>download</strong> these files, 
                but cannot edit them. Great for studying examples and reference code!
              </p>
            </div>
            <div style={{ backgroundColor: '#1f6feb', padding: '15px', borderRadius: '8px' }}>
              <h5 style={{ margin: '0 0 10px 0', color: '#ffffff' }}>📝 MY FILES</h5>
              <p style={{ margin: 0, color: '#c9d1d9', lineHeight: '1.8' }}>
                Your personal workspace! Create, edit, save, and organize your own Python files and folders.
                These are private to your account.
              </p>
            </div>
          </div>

          <h4 style={{ color: '#f0ad4e' }}>✨ Key Features</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <ul style={{ color: '#c9d1d9', lineHeight: '2.2', margin: 0 }}>
              <li><strong style={{ color: '#3fb950' }}>▶ Run Code:</strong> Execute Python files and see output instantly</li>
              <li><strong style={{ color: '#58a6ff' }}>💾 Save:</strong> Save your code to the cloud (MY FILES only)</li>
              <li><strong style={{ color: '#f0ad4e' }}>📝 Notes:</strong> Add personal notes to class files for studying</li>
              <li><strong style={{ color: '#8957e5' }}>💡 Explain:</strong> Get AI-generated explanations of any code</li>
              <li><strong style={{ color: '#1f6feb' }}>📥 Download:</strong> Download individual files or entire folders as ZIP</li>
              <li><strong style={{ color: '#166534' }}>🎓 Tutor Help:</strong> Get debugging help from the AI tutor (MY FILES)</li>
            </ul>
          </div>

          <h4 style={{ color: '#f0ad4e' }}>📁 Folder Management</h4>
          <div style={{ backgroundColor: '#1c2128', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <ul style={{ color: '#c9d1d9', lineHeight: '2.2', margin: 0 }}>
              <li><strong>Create folders:</strong> Right-click in MY FILES → New Folder</li>
              <li><strong>Create files:</strong> Right-click on a folder → New File</li>
              <li><strong>Drag dividers:</strong> Resize panels by dragging the dividers between sections</li>
              <li><strong>Collapse sections:</strong> Click ▼/▶ to collapse or expand file sections</li>
            </ul>
          </div>

          <div style={{ backgroundColor: '#3fb95022', padding: '15px', borderRadius: '8px' }}>
            <p style={{ color: '#3fb950', margin: 0 }}>
              💡 <strong>Pro Tip:</strong> Use the Tutor Help button when working on your own code to get 
              Socratic debugging assistance - the tutor will guide you to find bugs yourself!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "🎉 You're Ready!",
      content: (
        <div>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <span style={{ fontSize: '80px' }}>🎉</span>
            <h2 style={{ color: '#3fb950', marginTop: '20px' }}>You're All Set!</h2>
            <p style={{ color: '#8b949e', fontSize: '18px' }}>You now know everything to get the most out of Python Tutor</p>
          </div>

          <div style={{ backgroundColor: '#1c2128', padding: '25px', borderRadius: '12px', marginBottom: '25px' }}>
            <h4 style={{ color: '#58a6ff', marginTop: 0 }}>Quick Reference</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div style={{ backgroundColor: '#0d1117', padding: '12px', borderRadius: '6px' }}>
                <span style={{ color: '#238636' }}>📋</span> <strong>Breakdown</strong>
                <p style={{ color: '#8b949e', margin: '5px 0 0', fontSize: '13px' }}>New problems → steps</p>
              </div>
              <div style={{ backgroundColor: '#0d1117', padding: '12px', borderRadius: '6px' }}>
                <span style={{ color: '#1f6feb' }}>🔧</span> <strong>Debug</strong>
                <p style={{ color: '#8b949e', margin: '5px 0 0', fontSize: '13px' }}>Fix broken code</p>
              </div>
              <div style={{ backgroundColor: '#0d1117', padding: '12px', borderRadius: '6px' }}>
                <span style={{ color: '#8957e5' }}>🧱</span> <strong>Building Blocks</strong>
                <p style={{ color: '#8b949e', margin: '5px 0 0', fontSize: '13px' }}>Practice concepts</p>
              </div>
              <div style={{ backgroundColor: '#0d1117', padding: '12px', borderRadius: '6px' }}>
                <span style={{ color: '#f0ad4e' }}>💡</span> <strong>Hint</strong>
                <p style={{ color: '#8b949e', margin: '5px 0 0', fontSize: '13px' }}>When stuck</p>
              </div>
              <div style={{ backgroundColor: '#0d1117', padding: '12px', borderRadius: '6px' }}>
                <span style={{ color: '#3fb950' }}>📁</span> <strong>Class Codes</strong>
                <p style={{ color: '#8b949e', margin: '5px 0 0', fontSize: '13px' }}>View & manage files</p>
              </div>
              <div style={{ backgroundColor: '#0d1117', padding: '12px', borderRadius: '6px' }}>
                <span style={{ color: '#166534' }}>🎓</span> <strong>Tutor Help</strong>
                <p style={{ color: '#8b949e', margin: '5px 0 0', fontSize: '13px' }}>Debugging assistance</p>
              </div>
            </div>
          </div>

          <div style={{ backgroundColor: '#238636', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '18px' }}>
              🚀 Close this tutorial and start learning! Good luck!
            </p>
          </div>

          <p style={{ color: '#8b949e', textAlign: 'center', marginTop: '20px', fontStyle: 'italic' }}>
            You can open this tutorial anytime by clicking "📖 Tutorial" near the logout button.
          </p>
        </div>
      )
    }
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(13, 17, 23, 0.98)',
      zIndex: 2000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px',
      boxSizing: 'border-box'
    }}>
      <div style={{
        backgroundColor: '#0d1117',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(254, 249, 195, 0.35)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 25px',
          borderBottom: '1px solid #30363d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#fff', fontSize: '24px' }}>
              📖 {sections[currentSection].title}
            </h2>
            <p style={{ margin: '5px 0 0', color: '#8b949e', fontSize: '14px' }}>
              Section {currentSection + 1} of {sections.length}
            </p>
          </div>
          <button
            onClick={() => { window.scrollTo(0, 0); onClose(); }}
            style={{
              background: 'none',
              border: 'none',
              color: '#c9d1d9',
              fontSize: '28px',
              cursor: 'pointer',
              padding: '5px',
              lineHeight: 1
            }}
            onMouseOver={(e) => e.target.style.color = '#f85149'}
            onMouseOut={(e) => e.target.style.color = '#c9d1d9'}
          >
            ✕
          </button>
        </div>

        {/* Progress Bar */}
        <div style={{ padding: '0 25px', marginTop: '15px' }}>
          <div style={{
            display: 'flex',
            gap: '4px',
            marginBottom: '10px'
          }}>
            {sections.map((_, idx) => (
              <div
                key={idx}
                onClick={() => setCurrentSection(idx)}
                style={{
                  flex: 1,
                  height: '4px',
                  backgroundColor: idx <= currentSection ? '#238636' : '#30363d',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s'
                }}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px 25px',
          color: '#c9d1d9'
        }}>
          {sections[currentSection].content}
        </div>

        {/* Navigation Footer */}
        <div style={{
          padding: '20px 25px',
          borderTop: '1px solid #30363d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={() => setCurrentSection(prev => Math.max(0, prev - 1))}
            disabled={currentSection === 0}
            style={{
              padding: '10px 20px',
              backgroundColor: currentSection === 0 ? '#21262d' : '#30363d',
              color: currentSection === 0 ? '#484f58' : '#c9d1d9',
              border: 'none',
              borderRadius: '6px',
              cursor: currentSection === 0 ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            ← Previous
          </button>

          <div style={{ display: 'flex', gap: '8px' }}>
            {sections.map((_, idx) => (
              <div
                key={idx}
                onClick={() => setCurrentSection(idx)}
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: idx === currentSection ? '#238636' : '#30363d',
                  cursor: 'pointer'
                }}
              />
            ))}
          </div>

          {currentSection === sections.length - 1 ? (
            <button
              onClick={() => { window.scrollTo(0, 0); onClose(); }}
              style={{
                padding: '10px 25px',
                backgroundColor: '#238636',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              Start Learning! 🚀
            </button>
          ) : (
            <button
              onClick={() => setCurrentSection(prev => Math.min(sections.length - 1, prev + 1))}
              style={{
                padding: '10px 25px',
                backgroundColor: '#238636',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tutorial;
